import t from "@/lang/lang";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/shared/components/ui/table";
import { TableCard } from "@/shared/components/tables/table-card";
import { TableLoadingRow } from "@/shared/components/tables/table-loading-row";
import { TableRtkErrorRow } from "@/shared/components/tables/table-rtk-error-row";
import { TableEmptyRow } from "@/shared/components/tables/table-empty-row";
import { PaginationLimitOffset } from "@/shared/components/tables/pagination-limit-offset";
import { SearchFilter } from "@/shared/components/filter/search-filter";
import { CompanyFilter } from "@/services/backend/company/components/company-filter";
import { CompanyName } from "@/services/backend/company/components/company-name";
import { ReactNode } from "react";
import { Button } from "@/shared/components/ui/button";
import { AhuComponentCatalog } from "@/services/backend/htz/ahu/types/ahu-component-catalog";
import { ActiveAhuComponentCatalogCheckbox } from "@/services/backend/htz/ahu/components/active-ahu-component-catalog-checkbox";
import { DeleteComponentCatalogDialog } from "@/services/backend/htz/ahu/components/delete-component-catalog-dialog";
import {
  ListRequest,
  useHtzAhuComponentCatalogListQuery,
} from "@/services/backend/htz/ahu/api/ahu-component-catalog-api";

export function AhuComponentCatalogTable({
  request,
  onRequestChange,
  renderRowLink,
  hidePagination = false,
  hideBorder = false,
}: {
  request: ListRequest;
  onRequestChange: (request: ListRequest) => void;
  renderRowLink: (catalogId: string, children: ReactNode) => ReactNode;
  hidePagination?: boolean;
  hideBorder?: boolean;
}) {
  const { data, isLoading, error } =
    useHtzAhuComponentCatalogListQuery(request);

  const emptyList = !isLoading && !error && data?.items.length === 0;

  return (
    <div className="grid gap-2">
      <TableCard className={hideBorder ? "border-none shadow-none" : ""}>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t("Aktiv")}</TableHead>
              <TableHead>{t("Katalogname")}</TableHead>
              <TableHead>{t("Unternehmen")}</TableHead>
              <TableHead>{t("Aktionen")}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading && <TableLoadingRow colSpan={4} />}
            {error && <TableRtkErrorRow error={error} colSpan={4} />}
            {emptyList && <TableEmptyRow colSpan={4} />}
            {data &&
              data.items.map((catalog) => (
                <AhuComponentCatalogTableRow
                  key={catalog.id}
                  catalog={catalog}
                  renderRowLink={renderRowLink}
                />
              ))}
          </TableBody>
        </Table>
      </TableCard>
      {!hidePagination && data && !emptyList && (
        <div className="flex items-center">
          <PaginationLimitOffset
            offset={request.offset ?? 0}
            limit={request.limit ?? 0}
            totalCount={data.totalCount}
            setLimitOffset={(limit, offset) =>
              onRequestChange({ ...request, limit, offset })
            }
            className="ml-auto"
          />
        </div>
      )}
    </div>
  );
}

function AhuComponentCatalogTableRow({
  catalog,
  renderRowLink,
}: {
  catalog: AhuComponentCatalog;
  renderRowLink: (catalogId: string, children: ReactNode) => ReactNode;
}) {
  return (
    <TableRow>
      <TableCell>
        <div className="ml-4">
          <ActiveAhuComponentCatalogCheckbox
            companyId={catalog.companyId}
            catalogId={catalog.id}
          />
        </div>
      </TableCell>
      <TableCell>
        {renderRowLink(
          catalog.id,
          <Button variant="link">{catalog.name}</Button>,
        )}
      </TableCell>
      <TableCell>
        <CompanyName companyId={catalog.companyId} />
      </TableCell>
      <TableCell>
        <DeleteComponentCatalogDialog catalog={catalog} />
      </TableCell>
    </TableRow>
  );
}

export function AhuComponentCatalogTableFilters({
  request,
  onRequestChange,
  hideCompanyFilter,
}: {
  request: ListRequest;
  onRequestChange: (request: ListRequest) => void;
  hideCompanyFilter?: boolean;
}) {
  return (
    <div className="flex items-center gap-2">
      <SearchFilter
        filter={request.name || { active: false, values: [] }}
        onFilterChange={(name) => onRequestChange({ ...request, name })}
        placeholder={t("Nach Name suchen ...")}
        className="h-8"
      />
      <CompanyFilter
        filter={request.company ?? { active: false, values: [] }}
        onFilterChange={(company) => onRequestChange({ ...request, company })}
        hidden={hideCompanyFilter}
      />
    </div>
  );
}

export function useDefaultHtzAhuComponentCatalogListRequest(): ListRequest {
  return {
    name: { active: false, values: [] },
    company: { active: false, values: [] },
    limit: 50,
    offset: 0,
  };
}
